import type { UniqueIdentifier } from '@dnd-kit/core';
import { Card, CardButton } from '@payaca/ambrosia';
import { FC, useMemo } from 'react';

import ImageBlock from '@payaca/components/imageBlock/ImageBlock';
import MarkdownLabel from '@payaca/components/markdownLabel/MarkdownLabel';

import StatusBadge, {
  StatusBadgeState,
} from '@payaca/components/statusBadge/StatusBadge';
import { DragHandle } from '@payaca/dnd';

import { JobLineItem } from '@payaca/types/jobContentTypes';

import { useRegion } from '@/api/rest/users/useRegion';

import { currencyPrice } from '@payaca/helpers/financeHelper';

import { useJobLineItemAttachments } from '@payaca/store/hooks/appState';

import { roundToDecimalPlaces } from '@payaca/helpers/numberHelper';

type Props = {
  jobLineItem: JobLineItem;
  quantitySectionComponent?: React.ReactNode;
  onClick?: () => void;
  dragHandleId?: UniqueIdentifier;
};

const JobLineItemCard: FC<Props> = ({
  jobLineItem,
  onClick,
  quantitySectionComponent,
  dragHandleId,
}: Props) => {
  const region = useRegion();
  const jobLineItemAttachments = useJobLineItemAttachments(jobLineItem);
  const imageSrc = jobLineItemAttachments?.[0]?.attachmentUrl;

  const totals = useMemo(() => {
    const vatMultiplier =
      1 +
      ((jobLineItem.vatIncluded &&
        !jobLineItem.isReverseChargeVat &&
        jobLineItem.vatAmount / 100) ||
        0);
    let totalExcVat = jobLineItem.calculatedPrice * jobLineItem.quantity;
    let totalIncVat = totalExcVat * vatMultiplier;
    let cisTotal = jobLineItem.cisDeductionRate
      ? (jobLineItem.calculatedPrice *
          jobLineItem.quantity *
          jobLineItem.cisDeductionRate) /
        100
      : null;

    if (jobLineItem.discountPercentage || jobLineItem.markupPercentage) {
      totalExcVat = jobLineItem.subtotalIncMarkupDiscount;
      totalIncVat =
        jobLineItem.subtotalIncMarkupDiscount + jobLineItem.vatTotal;
      cisTotal = jobLineItem.cisTotal || null;
    }

    if (cisTotal) {
      totalIncVat = totalIncVat - cisTotal;
    }

    return { totalIncVat, totalExcVat, cisTotal };
  }, [jobLineItem]);

  const cardContent = (
    <>
      {/* Drag handle */}
      {dragHandleId && (
        <DragHandle
          as="div"
          id={dragHandleId}
          label={jobLineItem.name ?? 'Untitled item'}
        />
      )}

      {/* Image thumbnail */}
      <div className="shrink-0 [&_.image-block]:h-10 [&_.image-block]:w-10">
        <ImageBlock imageSrc={imageSrc} />
      </div>

      {/* Stacks name above metrics below md; rejoins the parent row at md via contents */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 md:contents">
        {/* Name and description */}
        <div className="min-w-0 md:flex-1">
          <h4 className="mb-0">{jobLineItem.name}</h4>
          <MarkdownLabel
            className="mb-0 text-sm text-gray-600"
            markdown={jobLineItem.description}
            limitLines={3}
          />
        </div>

        <div className="flex items-center justify-end gap-3 md:contents">
          {/* Badges - only show if present */}
          {(!!jobLineItem.discountPercentage ||
            !!jobLineItem.markupPercentage) && (
            <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
              {!!jobLineItem.discountPercentage && (
                <StatusBadge state={StatusBadgeState.GREY_INITIAL}>
                  {`${jobLineItem.discountPercentage}% discount`}
                </StatusBadge>
              )}
              {!!jobLineItem.markupPercentage && (
                <StatusBadge state={StatusBadgeState.GHOST}>
                  {`${roundToDecimalPlaces(
                    jobLineItem.markupPercentage,
                    1
                  )}% markup`}
                </StatusBadge>
              )}
            </div>
          )}

          {/* Totals */}
          <div className="flex w-24 shrink-0 flex-col items-end">
            <span className="text-sm font-medium text-gray-900">
              {currencyPrice(totals.totalIncVat, region)}
            </span>
            <span className="text-xs text-gray-500">
              {currencyPrice(totals.totalExcVat, region)} ex tax
            </span>
            {totals.cisTotal && (
              <span className="text-xs text-gray-500">
                {currencyPrice(totals.cisTotal, region)} CIS
              </span>
            )}
          </div>

          {/* Quantity */}
          <div className="flex w-16 shrink-0 flex-col items-center border-l border-gray-200 pl-3">
            {quantitySectionComponent ? (
              quantitySectionComponent
            ) : (
              <>
                <span className="text-xs text-gray-500">Qty</span>
                <span className="text-sm font-medium text-gray-900">
                  {jobLineItem.quantity}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <CardButton
        onClick={onClick}
        sizeVariant="sm"
        className="min-w-0"
        data-testid="job-line-item-card"
      >
        <Card.Body className="flex flex-row items-center gap-3">
          {cardContent}
        </Card.Body>
      </CardButton>
    );
  }

  return (
    <Card className="w-full" sizeVariant="sm" data-testid="job-line-item-card">
      <Card.Body className="flex items-center gap-3">{cardContent}</Card.Body>
    </Card>
  );
};

export default JobLineItemCard;
